import React, { useState, useEffect } from 'react';
import i18nIsoCountries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import CustomTable from 'components/CustomTable/CustomTable';
import { theme, Button, notification, Modal } from 'antd';
import { article8Op } from 'store/ducks/DataManagement/Article8AndICTSystem';
import { IICTRiskType } from 'store/ducks/DataManagement/Article8AndICTSystem/types';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { getICTRiskColumns } from 'components/Columns/ICTRiskColumns';
import SaveButton from 'components/SaveButton/SaveButton';
import useFilters from 'utils/hooks/useFilters';
import { useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import * as Styled from '../../DataEntry/DataEntry.styled';
import { useTranslation } from 'react-i18next';
import { deleteRecord } from 'utils/hooks/deleteRecord';

const ICTRisk: React.FC = () => {
  i18nIsoCountries.registerLocale(enLocale);
  const { query } = useFilters();
  const { useToken } = theme;
  const { t } = useTranslation();
  const [dataSource, setDataSource] = useState<IICTRiskType[]>([]);
  const [count, setCount] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const { token } = useToken();
  const isLoggedIn = localStorage.getItem('authToken');
  const [riskExposures, setRiskExposures] = useState<
    { label: string; value: string }[]
  >([]);
  const [riskLevels, setRiskLevels] = useState<
    { label: string; value: string }[]
  >([]);
  const [riskSources, setRiskSources] = useState<
    { label: string; value: string }[]
  >([]);
  const [ictSystems, setICTSystems] = useState<
    { label: string; value: string }[]
  >([]);
  const { selectedSystem } = useSelector((state: RootState) => state.article8);
  const { confirm } = Modal;
  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleDateChange = (key: number, field: string, dateString: string) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => key === item.key);
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, {
        ...item,
        [field]: dateString,
        isEdited: !item.isNew,
      });
      setDataSource(newData);
    }
  };

  const fetchRiskExposure = async () => {
    try {
      const response = await article8Op.getRiskExposure();
      if (response?.data.risk_exposures) {
        const formattedOptions = response.data.risk_exposures.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setRiskExposures(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchRiskLevel = async () => {
    try {
      const response = await article8Op.getRiskLevel();
      if (response?.data.risk_levels) {
        const formattedOptions = response.data.risk_levels.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setRiskLevels(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchRiskSource = async () => {
    try {
      const response = await article8Op.getRiskSource();
      if (response?.data.risk_sources) {
        const formattedOptions = response.data.risk_sources.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setRiskSources(formattedOptions);
      }
    } catch (error) {
      console.log('Failed to fetch risk sources:', error);
    }
  };

  const fetchICTRiskByEntity = async (system: string | undefined) => {
    try {
      let response;

      if (system === 'FUNCTION') {
        response = await article8Op.getICTRiskFunctionDetails(query.entityId);
      } else if (system === 'ICT_SERVICE') {
        response = await article8Op.getICTRiskServiceDetails(query.entityId);
      }

      const data = response?.data?.ict_risks;

      const mappedData = Array.isArray(data)
        ? data.map((item, index) => ({
            key: index,
            active: item.active,
            deleted: item.deleted,
            start_date: item.risk_start_date,
            end_date: item.risk_end_date,
            description: item.risk_description,
            id: item.risk_id,
            risk_name: item.risk_name,
            risk_exposure_id: item.risk_exposure_id,
            risk_exposure_name: item.risk_exposure_name,
            risk_level_id: item.risk_level_id,
            risk_level_name: item.risk_level_name,
            risk_source_id: item.risk_source_id,
            risk_source_name: item.risk_source_name,
            entity_id: item.entity_id,
            entity_name: item.entity_name,
            ict_system_id: item.ict_system_id,
            ict_system_name: item.ict_system_name,
            isNew: false,
            isEdited: false,
          }))
        : [];

      setDataSource(mappedData);
      setCount(mappedData.length);
    } catch (error) {
      setDataSource([]);
    }
  };

  useEffect(() => {
    if (!query.entityId || !selectedSystem) {
      setDataSource([]);
      setICTSystems([]);
      return;
    }

    const system = selectedSystem;

    setDataSource([]);
    setICTSystems([]);

    fetchICTRiskByEntity(system);
    fetchICTSystems(system);
    fetchRiskExposure();
    fetchRiskLevel();
    fetchRiskSource();
  }, [query.entityId, query.entity, selectedSystem]);

  const fetchICTSystems = async (system: string) => {
    try {
      let response;

      if (system === 'ICT_SERVICE') {
        response = await article8Op.fetchICTSystemsByICTServices(
          query.entityId,
        );
      } else if (system === 'FUNCTION') {
        response = await article8Op.fetchICTSystemsByFunctions(query.entityId);
      }

      if (response?.data?.ict_systems) {
        const formattedOptions = response.data.ict_systems.map(
          (item: { name: any; ict_system_id: any }) => ({
            label: item.name,
            value: item.ict_system_id,
          }),
        );

        setICTSystems(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddRow = (record?: any, index?: number) => {
    const newData: IICTRiskType = {
      key: generateUniqueId(),
      active: true,
      deleted: false,
      start_date: '',
      end_date: '9999-01-01',
      description: '',
      id: generateUniqueId(),
      risk_name: '',
      risk_exposure_id: '',
      risk_exposure_name: '',
      risk_level_id: '',
      risk_level_name: '',
      risk_source_id: '',
      risk_source_name: '',
      entity_id: query.entityId,
      entity_name: query.entity,
      ict_system_id: '',
      ict_system_name: '',
      isNew: true,
    };
    const insertIndex = dataSource.findIndex(
      (item) => item.key === record?.key,
    );

    const updatedDataSource = [
      ...dataSource.slice(0, insertIndex + 1),
      newData,
      ...dataSource.slice(insertIndex + 1),
    ];

    setDataSource(updatedDataSource);
    setCount(count + 1);
  };

  const handleSave = (row: IICTRiskType) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    newData.splice(index, 1, {
      ...newData[index],
      ...row,
      isEdited: !newData[index].isNew,
    });
    setDataSource(newData);
  };

  const handleRemoveRow = (key: number) => {
    Modal.confirm({
      title: 'Are you sure you want to remove this row?',
      content:
        'Once deleted, this record cannot be restored. Would you like to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        const updatedDataSource = dataSource.filter((item) => item.key !== key);
        setDataSource(updatedDataSource);
        notification.success({
          message: 'Info',
          description: 'The row has been successfully removed.',
          duration: 20,
        });
      },
      onCancel: () => {
        notification.info({
          message: 'Info',
          description: 'The row removal has been canceled.',
          duration: 20,
        });
      },
    });
  };

  const handleInactivate = (record: IICTRiskType) => {
    if (!record.id) {
      notification.error({
        message: 'Info',
        description: 'Id is missing.',
      });
      return;
    }

    deleteRecord({
      id: record.id,
      tableName: 'ict_risk',
      onSuccess: () => {
        fetchICTRiskByEntity(selectedSystem);
      },
    });
  };

  const columns = getICTRiskColumns(
    handleDateChange,
    handleSave,
    handleAddRow,
    handleRemoveRow,
    handleInactivate,
    riskExposures,
    riskLevels,
    riskSources,
    ictSystems,
    t,
  );

  const handleSubmit = async () => {
    const changedData = dataSource.filter(
      (item) => item.isNew || item.isEdited,
    );

    if (changedData.length === 0) {
      notification.warning({
        message: 'Info',
        description: 'There are no new or edited data to submit.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }
    const formattedData = {
      data_list: changedData.map((item) => ({
        id: item.id,
        name: item.risk_name,
        description: item.description,
        start_date: item.start_date,
        end_date: item.end_date,
        risk_source_id: item.risk_source_id,
        risk_level_id: item.risk_level_id,
        risk_exposure_id: item.risk_exposure_id,
        ict_system_id: item.ict_system_id,
      })),
      token: isLoggedIn,
    };

    try {
      const response = await article8Op.insertICTRisk(formattedData);
      if (response?.status === 200) {
        setDataSource((prev) =>
          prev.map((item) =>
            item.isNew || item.isEdited
              ? { ...item, isNew: false, isEdited: false }
              : item,
          ),
        );
        notification.success({
          message: 'Info',
          description: 'The data was successfully submitted.',
          placement: 'topRight',
          duration: 20,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };

  return (
    dataSource && (
      <>
        <Styled.HighlightedTable>
          <CustomTable
            columns={columns}
            dataSource={dataSource}
            handleSave={handleSave}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              onChange: handlePageChange,
              showSizeChanger: true,
            }}
            locale={{
              emptyText: (
                <Button type="primary" onClick={() => handleAddRow()}>
                  {t('GENERAL.ADD_FIRST_ROW')}
                </Button>
              ),
            }}
          />
        </Styled.HighlightedTable>

        <Styled.ButtonRow>
          {dataSource.length > 0 && (
            <Styled.AreaButton
              token={token}
              type="primary"
              onClick={() => handleAddRow()}
            >
              Add new row
            </Styled.AreaButton>
          )}
          {dataSource.length > 0 && (
            <SaveButton
              token={token}
              hasChanges={dataSource.some((i) => i.isNew || i.isEdited)}
              onClick={handleSubmit}
            />
          )}
        </Styled.ButtonRow>
      </>
    )
  );
};

export default ICTRisk;
